/**
 * Texts of the school manager's page (SchoolView.tsx, the Unity app's
 * "Manage Teachers": accManageTeachersButton, infoManageTeachers…) and of the
 * school licence on the account and subscription pages, in the web's 35 languages.
 */
import { currentLanguage } from "../player/i18n";

type Key =
  | "manage"
  | "intro"
  | "places"
  | "validUntil"
  | "expired"
  | "more"
  | "schoolName"
  | "rename"
  | "add"
  | "email"
  | "nameOpt"
  | "addBtn"
  | "remove"
  | "removeTitle"
  | "removeText"
  | "signedUp"
  | "notYet"
  | "none"
  | "errSeats"
  | "errOther"
  | "errManager"
  | "errEmail"
  | "managerLine";

const TEXTS: Record<string, Record<Key, string>> = {
  ca: {
    manage: "Gestionar mestres",
    intro: "La teva llicència d'escola et permet tenir {seats} mestres a l'equip, a més de tu. Afegeix-los amb el seu correu: seran Premium quan entrin a Make It amb aquest correu, a la web o a l'app.",
    places: "{used} de {seats} places ocupades",
    validUntil: "Llicència vàlida fins al {date}.",
    expired: "La llicència va caducar el {date}. Per renovar-la, escriu-nos a {email}.",
    more: "Necessites més places? Escriu-nos a {email}.",
    schoolName: "Nom de l'escola",
    rename: "Canvia el nom",
    add: "Afegeix un mestre",
    email: "Correu",
    nameOpt: "Nom (opcional)",
    addBtn: "Afegeix",
    remove: "Treu",
    removeTitle: "Vols treure {email}?",
    removeText: "Deixarà de ser Premium amb aquesta llicència i la plaça quedarà lliure. Els seus projectes no s'esborren.",
    signedUp: "Ja ha entrat",
    notYet: "Encara no ha entrat",
    none: "Encara no hi ha cap mestre.",
    errSeats: "No queden places lliures.",
    errOther: "Aquest correu ja és d'una altra escola.",
    errManager: "Aquest correu és el del gestor de l'escola.",
    errEmail: "Escriu un correu vàlid.",
    managerLine: "Llicència d'escola: tu gestiones els mestres.",
  },
  es: {
    manage: "Gestionar profesores",
    intro: "Tu licencia de escuela te permite tener {seats} profesores en el equipo, además de ti. Añádelos con su correo: serán Premium cuando entren en Make It con ese correo, en la web o en la app.",
    places: "{used} de {seats} plazas ocupadas",
    validUntil: "Licencia válida hasta el {date}.",
    expired: "La licencia caducó el {date}. Para renovarla, escríbenos a {email}.",
    more: "¿Necesitas más plazas? Escríbenos a {email}.",
    schoolName: "Nombre de la escuela",
    rename: "Cambiar el nombre",
    add: "Añadir un profesor",
    email: "Correo",
    nameOpt: "Nombre (opcional)",
    addBtn: "Añadir",
    remove: "Quitar",
    removeTitle: "¿Quitar a {email}?",
    removeText: "Dejará de ser Premium con esta licencia y la plaza quedará libre. Sus proyectos no se borran.",
    signedUp: "Ya ha entrado",
    notYet: "Aún no ha entrado",
    none: "Todavía no hay ningún profesor.",
    errSeats: "No quedan plazas libres.",
    errOther: "Este correo ya es de otra escuela.",
    errManager: "Este correo es el del gestor de la escuela.",
    errEmail: "Escribe un correo válido.",
    managerLine: "Licencia de escuela: tú gestionas los profesores.",
  },
  en: {
    manage: "Manage teachers",
    intro: "Your school licence lets you have {seats} teachers on your team, besides you. Add them by e-mail: they'll be Premium when they sign in to Make It with that e-mail, on the web or in the app.",
    places: "{used} of {seats} seats taken",
    validUntil: "Licence valid until {date}.",
    expired: "The licence expired on {date}. To renew it, write to us at {email}.",
    more: "Need more seats? Write to us at {email}.",
    schoolName: "School name",
    rename: "Rename",
    add: "Add a teacher",
    email: "E-mail",
    nameOpt: "Name (optional)",
    addBtn: "Add",
    remove: "Remove",
    removeTitle: "Remove {email}?",
    removeText: "They will no longer be Premium through this licence and the seat will be free. Their projects are not deleted.",
    signedUp: "Signed in",
    notYet: "Not signed in yet",
    none: "No teachers yet.",
    errSeats: "There are no free seats left.",
    errOther: "This e-mail already belongs to another school.",
    errManager: "This e-mail is the school manager's.",
    errEmail: "Enter a valid e-mail.",
    managerLine: "School licence: you manage the teachers.",
  },
  fr: {
    manage: "Gérer les professeurs",
    intro: "Votre licence d'école vous permet d'avoir {seats} professeurs dans votre équipe, en plus de vous. Ajoutez-les avec leur e-mail : ils seront Premium dès qu'ils se connecteront à Make It avec cet e-mail, sur le web ou dans l'app.",
    places: "{used} places occupées sur {seats}",
    validUntil: "Licence valable jusqu'au {date}.",
    expired: "La licence a expiré le {date}. Pour la renouveler, écrivez-nous à {email}.",
    more: "Besoin de plus de places ? Écrivez-nous à {email}.",
    schoolName: "Nom de l'école",
    rename: "Renommer",
    add: "Ajouter un professeur",
    email: "E-mail",
    nameOpt: "Nom (facultatif)",
    addBtn: "Ajouter",
    remove: "Retirer",
    removeTitle: "Retirer {email} ?",
    removeText: "Cette personne ne sera plus Premium grâce à cette licence et la place sera libérée. Ses projets ne sont pas supprimés.",
    signedUp: "Déjà connecté",
    notYet: "Pas encore connecté",
    none: "Aucun professeur pour l'instant.",
    errSeats: "Il n'y a plus de places libres.",
    errOther: "Cet e-mail appartient déjà à une autre école.",
    errManager: "Cet e-mail est celui du gestionnaire de l'école.",
    errEmail: "Saisissez un e-mail valide.",
    managerLine: "Licence d'école : vous gérez les professeurs.",
  },
  pt: {
    manage: "Gerir professores",
    intro: "A tua licença de escola permite-te ter {seats} professores na equipa, além de ti. Adiciona-os com o e-mail: serão Premium quando entrarem no Make It com esse e-mail, na web ou na app.",
    places: "{used} de {seats} lugares ocupados",
    validUntil: "Licença válida até {date}.",
    expired: "A licença expirou a {date}. Para a renovar, escreve-nos para {email}.",
    more: "Precisas de mais lugares? Escreve-nos para {email}.",
    schoolName: "Nome da escola",
    rename: "Mudar o nome",
    add: "Adicionar um professor",
    email: "E-mail",
    nameOpt: "Nome (opcional)",
    addBtn: "Adicionar",
    remove: "Retirar",
    removeTitle: "Retirar {email}?",
    removeText: "Deixará de ser Premium com esta licença e o lugar fica livre. Os seus projetos não são apagados.",
    signedUp: "Já entrou",
    notYet: "Ainda não entrou",
    none: "Ainda não há professores.",
    errSeats: "Não há lugares livres.",
    errOther: "Este e-mail já pertence a outra escola.",
    errManager: "Este e-mail é o do gestor da escola.",
    errEmail: "Escreve um e-mail válido.",
    managerLine: "Licença de escola: és tu quem gere os professores.",
  },
  it: {
    manage: "Gestisci gli insegnanti",
    intro: "La tua licenza scolastica ti permette di avere {seats} insegnanti nel team, oltre a te. Aggiungili con la loro e-mail: saranno Premium quando accederanno a Make It con quella e-mail, sul web o nell'app.",
    places: "{used} posti occupati su {seats}",
    validUntil: "Licenza valida fino al {date}.",
    expired: "La licenza è scaduta il {date}. Per rinnovarla, scrivici a {email}.",
    more: "Ti servono più posti? Scrivici a {email}.",
    schoolName: "Nome della scuola",
    rename: "Rinomina",
    add: "Aggiungi un insegnante",
    email: "E-mail",
    nameOpt: "Nome (facoltativo)",
    addBtn: "Aggiungi",
    remove: "Rimuovi",
    removeTitle: "Rimuovere {email}?",
    removeText: "Non sarà più Premium con questa licenza e il posto sarà libero. I suoi progetti non vengono eliminati.",
    signedUp: "Ha già effettuato l'accesso",
    notYet: "Non ha ancora effettuato l'accesso",
    none: "Ancora nessun insegnante.",
    errSeats: "Non ci sono più posti liberi.",
    errOther: "Questa e-mail appartiene già a un'altra scuola.",
    errManager: "Questa e-mail è quella del gestore della scuola.",
    errEmail: "Inserisci un'e-mail valida.",
    managerLine: "Licenza scolastica: gestisci tu gli insegnanti.",
  },
  ar: {
    manage: "إدارة المعلمين",
    intro: "تتيح لك رخصة المدرسة إضافة {seats} معلمين إلى فريقك، إلى جانبك. أضفهم ببريدهم الإلكتروني: سيحصلون على Premium عند تسجيل الدخول إلى Make It بهذا البريد، على الويب أو في التطبيق.",
    places: "{used} من {seats} مقاعد مشغولة",
    validUntil: "الرخصة صالحة حتى {date}.",
    expired: "انتهت صلاحية الرخصة في {date}. لتجديدها، راسلنا على {email}.",
    more: "هل تحتاج إلى مقاعد أكثر؟ راسلنا على {email}.",
    schoolName: "اسم المدرسة",
    rename: "تغيير الاسم",
    add: "إضافة معلم",
    email: "البريد الإلكتروني",
    nameOpt: "الاسم (اختياري)",
    addBtn: "إضافة",
    remove: "إزالة",
    removeTitle: "إزالة {email}؟",
    removeText: "لن يعود Premium عبر هذه الرخصة وسيصبح المقعد شاغرًا. لن تُحذف مشاريعه.",
    signedUp: "سجّل الدخول",
    notYet: "لم يسجّل الدخول بعد",
    none: "لا يوجد معلمون بعد.",
    errSeats: "لا توجد مقاعد شاغرة.",
    errOther: "هذا البريد الإلكتروني تابع لمدرسة أخرى بالفعل.",
    errManager: "هذا البريد الإلكتروني هو بريد مدير المدرسة.",
    errEmail: "أدخل بريدًا إلكترونيًا صالحًا.",
    managerLine: "رخصة مدرسة: أنت من يدير المعلمين.",
  },
  he: {
    manage: "ניהול מורים",
    intro: "רישיון בית הספר מאפשר {seats} מורים בצוות, בנוסף אליך. הוסיפו אותם לפי כתובת הדוא״ל: הם יקבלו Premium כשייכנסו ל-Make It עם הכתובת הזו, באתר או באפליקציה.",
    places: "{used} מתוך {seats} מקומות תפוסים",
    validUntil: "הרישיון בתוקף עד {date}.",
    expired: "תוקף הרישיון פג ב-{date}. לחידוש, כתבו לנו לכתובת {email}.",
    more: "צריכים עוד מקומות? כתבו לנו לכתובת {email}.",
    schoolName: "שם בית הספר",
    rename: "שינוי שם",
    add: "הוספת מורה",
    email: "דוא״ל",
    nameOpt: "שם (לא חובה)",
    addBtn: "הוספה",
    remove: "הסרה",
    removeTitle: "להסיר את {email}?",
    removeText: "לא יהיה עוד Premium דרך הרישיון הזה והמקום יתפנה. הפרויקטים לא יימחקו.",
    signedUp: "כבר נכנס/ה",
    notYet: "עדיין לא נכנס/ה",
    none: "עדיין אין מורים.",
    errSeats: "לא נשארו מקומות פנויים.",
    errOther: "כתובת הדוא״ל הזו כבר שייכת לבית ספר אחר.",
    errManager: "זו כתובת הדוא״ל של מנהל/ת בית הספר.",
    errEmail: "יש להזין כתובת דוא״ל תקינה.",
    managerLine: "רישיון בית ספר: את/ה מנהל/ת את המורים.",
  },
  de: {
    manage: "Lehrkräfte verwalten",
    intro: "Mit deiner Schullizenz kannst du außer dir {seats} Lehrkräfte im Team haben. Füge sie mit ihrer E-Mail-Adresse hinzu: Sie sind Premium, sobald sie sich mit dieser Adresse bei Make It anmelden, im Web oder in der App.",
    places: "{used} von {seats} Plätzen belegt",
    validUntil: "Lizenz gültig bis {date}.",
    expired: "Die Lizenz ist am {date} abgelaufen. Zum Verlängern schreib uns an {email}.",
    more: "Brauchst du mehr Plätze? Schreib uns an {email}.",
    schoolName: "Name der Schule",
    rename: "Umbenennen",
    add: "Lehrkraft hinzufügen",
    email: "E-Mail",
    nameOpt: "Name (optional)",
    addBtn: "Hinzufügen",
    remove: "Entfernen",
    removeTitle: "{email} entfernen?",
    removeText: "Die Person ist dann nicht mehr über diese Lizenz Premium, und der Platz wird frei. Ihre Projekte werden nicht gelöscht.",
    signedUp: "Bereits angemeldet",
    notYet: "Noch nicht angemeldet",
    none: "Noch keine Lehrkräfte.",
    errSeats: "Es sind keine Plätze mehr frei.",
    errOther: "Diese E-Mail-Adresse gehört schon zu einer anderen Schule.",
    errManager: "Diese E-Mail-Adresse gehört zur Verwaltung dieser Schule.",
    errEmail: "Gib eine gültige E-Mail-Adresse ein.",
    managerLine: "Schullizenz: Du verwaltest die Lehrkräfte.",
  },
  eu: {
    manage: "Kudeatu irakasleak",
    intro: "Zure ikastetxearen lizentziarekin {seats} irakasle izan ditzakezu taldean, zutaz gain. Gehitu itzazu helbide elektronikoaren bidez: Premium izango dira helbide horrekin Make It-en saioa hasten dutenean, webean edo aplikazioan.",
    places: "{seats} plazetatik {used} hartuta",
    validUntil: "Lizentziaren iraungitze-data: {date}.",
    expired: "Lizentzia iraungi egin zen: {date}. Berritzeko, idatzi iezaguzu helbide honetara: {email}.",
    more: "Plaza gehiago behar dituzu? Idatzi iezaguzu helbide honetara: {email}.",
    schoolName: "Ikastetxearen izena",
    rename: "Aldatu izena",
    add: "Gehitu irakasle bat",
    email: "Helbide elektronikoa",
    nameOpt: "Izena (aukerakoa)",
    addBtn: "Gehitu",
    remove: "Kendu",
    removeTitle: "{email} kendu?",
    removeText: "Ez da Premium izango lizentzia honen bidez, eta plaza libre geratuko da. Haren proiektuak ez dira ezabatuko.",
    signedUp: "Saioa hasita",
    notYet: "Oraindik ez du saioa hasi",
    none: "Oraindik ez dago irakaslerik.",
    errSeats: "Ez dago plaza librerik.",
    errOther: "Helbide elektroniko hau beste ikastetxe batena da jada.",
    errManager: "Helbide elektroniko hau ikastetxeko kudeatzailearena da.",
    errEmail: "Idatzi baliozko helbide elektroniko bat.",
    managerLine: "Ikastetxeko lizentzia: zuk kudeatzen dituzu irakasleak.",
  },
  gl: {
    manage: "Xestionar docentes",
    intro: "A licenza do teu centro permíteche ter {seats} docentes no teu equipo, ademais de ti. Engádeos co seu correo electrónico: serán Premium cando inicien sesión en Make It con ese correo, na web ou na aplicación.",
    places: "{used} de {seats} prazas ocupadas",
    validUntil: "Licenza válida ata o {date}.",
    expired: "A licenza caducou o {date}. Para renovala, escríbenos a {email}.",
    more: "Necesitas máis prazas? Escríbenos a {email}.",
    schoolName: "Nome do centro",
    rename: "Renomear",
    add: "Engadir un docente",
    email: "Correo electrónico",
    nameOpt: "Nome (opcional)",
    addBtn: "Engadir",
    remove: "Quitar",
    removeTitle: "Quitar {email}?",
    removeText: "Deixará de ser Premium con esta licenza e a praza quedará libre. Os seus proxectos non se eliminan.",
    signedUp: "Xa iniciou sesión",
    notYet: "Aínda non iniciou sesión",
    none: "Aínda non hai docentes.",
    errSeats: "Non quedan prazas libres.",
    errOther: "Este correo xa pertence a outro centro.",
    errManager: "Este correo é o do xestor do centro.",
    errEmail: "Escribe un correo válido.",
    managerLine: "Licenza de centro: ti xestionas os docentes.",
  },
  nl: {
    manage: "Leerkrachten beheren",
    intro: "Met je schoollicentie kun je naast jezelf {seats} leerkrachten in je team hebben. Voeg ze toe met hun e-mailadres: ze krijgen Premium zodra ze met dat e-mailadres inloggen bij Make It, op het web of in de app.",
    places: "{used} van {seats} plaatsen bezet",
    validUntil: "Licentie geldig tot {date}.",
    expired: "De licentie is verlopen op {date}. Mail ons op {email} om de licentie te verlengen.",
    more: "Meer plaatsen nodig? Mail ons op {email}.",
    schoolName: "Naam van de school",
    rename: "Hernoemen",
    add: "Leerkracht toevoegen",
    email: "E-mail",
    nameOpt: "Naam (optioneel)",
    addBtn: "Toevoegen",
    remove: "Verwijderen",
    removeTitle: "{email} verwijderen?",
    removeText: "Deze leerkracht heeft dan geen Premium meer via deze licentie en de plaats komt vrij. De projecten worden niet verwijderd.",
    signedUp: "Aangemeld",
    notYet: "Nog niet aangemeld",
    none: "Nog geen leerkrachten.",
    errSeats: "Er zijn geen vrije plaatsen meer.",
    errOther: "Dit e-mailadres hoort al bij een andere school.",
    errManager: "Dit is het e-mailadres van de schoolbeheerder.",
    errEmail: "Voer een geldig e-mailadres in.",
    managerLine: "Schoollicentie: jij beheert de leerkrachten.",
  },
  pl: {
    manage: "Zarządzaj nauczycielami",
    intro: "Twoja licencja szkolna pozwala mieć w zespole {seats} nauczycieli oprócz ciebie. Dodaj ich po adresie e-mail: otrzymają Premium, gdy zalogują się do Make It tym adresem, w przeglądarce lub w aplikacji.",
    places: "Zajęte miejsca: {used} z {seats}",
    validUntil: "Licencja ważna do {date}.",
    expired: "Licencja wygasła {date}. Aby ją odnowić, napisz do nas na {email}.",
    more: "Potrzebujesz więcej miejsc? Napisz do nas na {email}.",
    schoolName: "Nazwa szkoły",
    rename: "Zmień nazwę",
    add: "Dodaj nauczyciela",
    email: "E-mail",
    nameOpt: "Imię i nazwisko (opcjonalnie)",
    addBtn: "Dodaj",
    remove: "Usuń",
    removeTitle: "Usunąć {email}?",
    removeText: "Ta osoba nie będzie już mieć Premium w ramach tej licencji, a miejsce się zwolni. Jej projekty nie zostaną usunięte.",
    signedUp: "Zalogowano",
    notYet: "Jeszcze nie zalogowano",
    none: "Nie ma jeszcze nauczycieli.",
    errSeats: "Nie ma już wolnych miejsc.",
    errOther: "Ten adres e-mail należy już do innej szkoły.",
    errManager: "To adres e-mail osoby zarządzającej licencją szkoły.",
    errEmail: "Wpisz prawidłowy adres e-mail.",
    managerLine: "Licencja szkolna: zarządzasz nauczycielami.",
  },
  ro: {
    manage: "Gestionează profesorii",
    intro: "Licența școlii îți permite să ai {seats} profesori în echipă, pe lângă tine. Adaugă-i după adresa de e-mail: vor avea Premium când se conectează la Make It cu acel e-mail, pe web sau în aplicație.",
    places: "{used} din {seats} locuri ocupate",
    validUntil: "Licență valabilă până pe {date}.",
    expired: "Licența a expirat pe {date}. Ca s-o reînnoiești, scrie-ne la {email}.",
    more: "Ai nevoie de mai multe locuri? Scrie-ne la {email}.",
    schoolName: "Numele școlii",
    rename: "Redenumește",
    add: "Adaugă un profesor",
    email: "E-mail",
    nameOpt: "Nume (opțional)",
    addBtn: "Adaugă",
    remove: "Elimină",
    removeTitle: "Elimini {email}?",
    removeText: "Nu va mai avea Premium prin această licență, iar locul se va elibera. Proiectele sale nu se șterg.",
    signedUp: "Conectat",
    notYet: "Încă neconectat",
    none: "Încă nu ai profesori.",
    errSeats: "Nu mai sunt locuri libere.",
    errOther: "Acest e-mail aparține deja altei școli.",
    errManager: "Acesta este e-mailul administratorului școlii.",
    errEmail: "Introdu un e-mail valid.",
    managerLine: "Licență școlară: tu gestionezi profesorii.",
  },
  sv: {
    manage: "Hantera lärare",
    intro: "Med din skollicens kan du ha {seats} lärare i ditt team, utöver dig själv. Lägg till dem via e-post: de blir Premium när de loggar in i Make It med den e-postadressen, på webben eller i appen.",
    places: "{used} av {seats} platser används",
    validUntil: "Licensen gäller till den {date}.",
    expired: "Licensen gick ut den {date}. Skriv till oss på {email} för att förnya den.",
    more: "Behöver du fler platser? Skriv till oss på {email}.",
    schoolName: "Skolans namn",
    rename: "Byt namn",
    add: "Lägg till en lärare",
    email: "E-post",
    nameOpt: "Namn (valfritt)",
    addBtn: "Lägg till",
    remove: "Ta bort",
    removeTitle: "Ta bort {email}?",
    removeText: "Läraren är inte längre Premium via den här licensen och platsen blir ledig. Lärarens projekt raderas inte.",
    signedUp: "Har loggat in",
    notYet: "Har inte loggat in än",
    none: "Inga lärare än.",
    errSeats: "Det finns inga lediga platser kvar.",
    errOther: "Den här e-postadressen tillhör redan en annan skola.",
    errManager: "Den här e-postadressen tillhör skolans administratör.",
    errEmail: "Ange en giltig e-postadress.",
    managerLine: "Skollicens: du hanterar lärarna.",
  },
  el: {
    manage: "Διαχείριση εκπαιδευτικών",
    intro: "Η σχολική σου άδεια σού επιτρέπει να έχεις {seats} εκπαιδευτικούς στην ομάδα σου, εκτός από εσένα. Πρόσθεσέ τους με το email τους: θα γίνουν Premium όταν συνδεθούν στο Make It με αυτό το email, στον ιστότοπο ή στην εφαρμογή.",
    places: "Κατειλημμένες θέσεις: {used} από {seats}",
    validUntil: "Η άδεια ισχύει έως τις {date}.",
    expired: "Η άδεια έληξε στις {date}. Για να την ανανεώσεις, γράψε μας στο {email}.",
    more: "Χρειάζεσαι περισσότερες θέσεις; Γράψε μας στο {email}.",
    schoolName: "Όνομα σχολείου",
    rename: "Μετονομασία",
    add: "Προσθήκη εκπαιδευτικού",
    email: "Email",
    nameOpt: "Όνομα (προαιρετικό)",
    addBtn: "Προσθήκη",
    remove: "Αφαίρεση",
    removeTitle: "Αφαίρεση του {email};",
    removeText: "Δεν θα είναι πια Premium μέσω αυτής της άδειας και η θέση θα ελευθερωθεί. Τα έργα του δεν διαγράφονται.",
    signedUp: "Συνδέθηκε",
    notYet: "Δεν έχει συνδεθεί ακόμα",
    none: "Δεν υπάρχουν ακόμα εκπαιδευτικοί.",
    errSeats: "Δεν υπάρχουν άλλες ελεύθερες θέσεις.",
    errOther: "Αυτό το email ανήκει ήδη σε άλλο σχολείο.",
    errManager: "Αυτό είναι το email του διαχειριστή του σχολείου.",
    errEmail: "Γράψε ένα έγκυρο email.",
    managerLine: "Σχολική άδεια: εσύ διαχειρίζεσαι τους εκπαιδευτικούς.",
  },
  tr: {
    manage: "Öğretmenleri yönet",
    intro: "Okul lisansınız, siz hariç ekibinizde {seats} öğretmen bulundurmanıza olanak tanır. Öğretmenleri e-posta adresleriyle ekleyin: Make It'e bu e-postayla web'de veya uygulamada giriş yaptıklarında Premium olurlar.",
    places: "{used}/{seats} yer dolu",
    validUntil: "Lisans {date} tarihine kadar geçerli.",
    expired: "Lisansın süresi {date} tarihinde doldu. Yenilemek için {email} adresine yazın.",
    more: "Daha fazla yere mi ihtiyacınız var? {email} adresine yazın.",
    schoolName: "Okul adı",
    rename: "Yeniden adlandır",
    add: "Öğretmen ekle",
    email: "E-posta",
    nameOpt: "Ad (isteğe bağlı)",
    addBtn: "Ekle",
    remove: "Kaldır",
    removeTitle: "{email} kaldırılsın mı?",
    removeText: "Bu kişi artık bu lisans aracılığıyla Premium olmayacak ve yeri boşalacak. Projeleri silinmez.",
    signedUp: "Giriş yaptı",
    notYet: "Henüz giriş yapmadı",
    none: "Henüz öğretmen yok.",
    errSeats: "Boş yer kalmadı.",
    errOther: "Bu e-posta zaten başka bir okula ait.",
    errManager: "Bu e-posta okul yöneticisine ait.",
    errEmail: "Geçerli bir e-posta girin.",
    managerLine: "Okul lisansı: öğretmenleri siz yönetiyorsunuz.",
  },
  ru: {
    manage: "Управление учителями",
    intro: "Школьная лицензия позволяет вам добавить в команду до {seats} учителей, не считая вас. Добавляйте их по адресу эл. почты: они получат Premium, когда войдут в Make It с этим адресом на сайте или в приложении.",
    places: "Занято мест: {used} из {seats}",
    validUntil: "Лицензия действует до {date}.",
    expired: "Срок действия лицензии истёк {date}. Чтобы продлить её, напишите нам на {email}.",
    more: "Нужно больше мест? Напишите нам на {email}.",
    schoolName: "Название школы",
    rename: "Переименовать",
    add: "Добавить учителя",
    email: "Эл. почта",
    nameOpt: "Имя (необязательно)",
    addBtn: "Добавить",
    remove: "Удалить",
    removeTitle: "Удалить {email}?",
    removeText: "Этот учитель больше не будет получать Premium по этой лицензии, а место освободится. Его проекты не удаляются.",
    signedUp: "Вход выполнен",
    notYet: "Вход ещё не выполнен",
    none: "Учителей пока нет.",
    errSeats: "Свободных мест не осталось.",
    errOther: "Этот адрес уже принадлежит другой школе.",
    errManager: "Это адрес администратора школы.",
    errEmail: "Введите корректный адрес эл. почты.",
    managerLine: "Школьная лицензия: вы управляете учителями.",
  },
  uk: {
    manage: "Керування вчителями",
    intro: "Кількість учителів, яких ви можете мати в команді за шкільною ліцензією (крім вас): {seats}. Додавайте їх за електронною адресою: вони отримають Premium, щойно ввійдуть у Make It з цією адресою — на сайті або в застосунку.",
    places: "Зайнято місць: {used} з {seats}",
    validUntil: "Ліцензія дійсна до {date}.",
    expired: "Термін дії ліцензії закінчився {date}. Щоб поновити її, напишіть нам на {email}.",
    more: "Потрібно більше місць? Напишіть нам на {email}.",
    schoolName: "Назва школи",
    rename: "Перейменувати",
    add: "Додати вчителя",
    email: "Електронна пошта",
    nameOpt: "Ім’я (необов’язково)",
    addBtn: "Додати",
    remove: "Вилучити",
    removeTitle: "Вилучити {email}?",
    removeText: "Цей користувач більше не матиме Premium за цією ліцензією, а місце звільниться. Його проєкти не буде видалено.",
    signedUp: "Вхід виконано",
    notYet: "Вхід ще не виконано",
    none: "Учителів ще немає.",
    errSeats: "Вільних місць не залишилося.",
    errOther: "Ця електронна адреса вже належить іншій школі.",
    errManager: "Це електронна адреса адміністратора школи.",
    errEmail: "Введіть дійсну електронну адресу.",
    managerLine: "Шкільна ліцензія: ви керуєте вчителями.",
  },
  id: {
    manage: "Kelola guru",
    intro: "Lisensi sekolah Anda memungkinkan Anda memiliki {seats} guru dalam tim, selain Anda sendiri. Tambahkan mereka melalui email: mereka akan menjadi Premium saat masuk ke Make It dengan email tersebut, di web atau di aplikasi.",
    places: "{used} dari {seats} slot terpakai",
    validUntil: "Lisensi berlaku hingga {date}.",
    expired: "Lisensi telah berakhir pada {date}. Untuk memperpanjangnya, hubungi kami di {email}.",
    more: "Butuh lebih banyak slot? Hubungi kami di {email}.",
    schoolName: "Nama sekolah",
    rename: "Ganti nama",
    add: "Tambahkan guru",
    email: "Email",
    nameOpt: "Nama (opsional)",
    addBtn: "Tambah",
    remove: "Keluarkan",
    removeTitle: "Keluarkan {email}?",
    removeText: "Guru ini tidak akan lagi menjadi Premium melalui lisensi ini dan slotnya akan kosong kembali. Proyeknya tidak dihapus.",
    signedUp: "Sudah masuk",
    notYet: "Belum masuk",
    none: "Belum ada guru.",
    errSeats: "Tidak ada slot kosong yang tersisa.",
    errOther: "Email ini sudah terdaftar di sekolah lain.",
    errManager: "Email ini milik pengelola sekolah.",
    errEmail: "Masukkan email yang valid.",
    managerLine: "Lisensi sekolah: Anda yang mengelola guru.",
  },
  vi: {
    manage: "Quản lý giáo viên",
    intro: "Giấy phép trường học cho phép bạn có {seats} giáo viên trong nhóm, ngoài bạn ra. Hãy thêm họ bằng email: họ sẽ có Premium khi đăng nhập vào Make It bằng email đó, trên web hoặc trong ứng dụng.",
    places: "Đã dùng {used} trên {seats} suất",
    validUntil: "Giấy phép có hiệu lực đến {date}.",
    expired: "Giấy phép đã hết hạn vào {date}. Để gia hạn, hãy viết cho chúng tôi tại {email}.",
    more: "Cần thêm suất? Hãy viết cho chúng tôi tại {email}.",
    schoolName: "Tên trường",
    rename: "Đổi tên",
    add: "Thêm giáo viên",
    email: "Email",
    nameOpt: "Tên (không bắt buộc)",
    addBtn: "Thêm",
    remove: "Xóa",
    removeTitle: "Xóa {email}?",
    removeText: "Người này sẽ không còn Premium qua giấy phép này và suất đó sẽ được trả lại. Các dự án của họ không bị xóa.",
    signedUp: "Đã đăng nhập",
    notYet: "Chưa đăng nhập",
    none: "Chưa có giáo viên nào.",
    errSeats: "Không còn suất trống nào.",
    errOther: "Email này đã thuộc về một trường khác.",
    errManager: "Email này là của người quản lý trường.",
    errEmail: "Hãy nhập email hợp lệ.",
    managerLine: "Giấy phép trường học: bạn quản lý các giáo viên.",
  },
  cs: {
    manage: "Spravovat učitele",
    intro: "Vaše školní licence vám umožňuje mít v týmu kromě vás {seats} učitelů. Přidejte je podle e-mailu: Premium budou mít, jakmile se s tímto e-mailem přihlásí do Make It, na webu nebo v aplikaci.",
    places: "Obsazená místa: {used} z {seats}",
    validUntil: "Licence platí do {date}.",
    expired: "Platnost licence vypršela {date}. Pro obnovení nám napište na {email}.",
    more: "Potřebujete více míst? Napište nám na {email}.",
    schoolName: "Název školy",
    rename: "Přejmenovat",
    add: "Přidat učitele",
    email: "E-mail",
    nameOpt: "Jméno (nepovinné)",
    addBtn: "Přidat",
    remove: "Odebrat",
    removeTitle: "Odebrat {email}?",
    removeText: "Přes tuto licenci už nebude mít Premium a místo se uvolní. Projekty se nesmažou.",
    signedUp: "Přihlášeno",
    notYet: "Zatím nepřihlášeno",
    none: "Zatím žádní učitelé.",
    errSeats: "Nezbývají žádná volná místa.",
    errOther: "Tento e-mail už patří jiné škole.",
    errManager: "Tento e-mail patří správci školy.",
    errEmail: "Zadejte platný e-mail.",
    managerLine: "Školní licence: učitele spravujete vy.",
  },
  sk: {
    manage: "Spravovať učiteľov",
    intro: "Vaša školská licencia vám umožňuje mať v tíme okrem seba ďalších učiteľov – počet miest: {seats}. Pridajte ich cez e-mail: Premium získajú, keď sa s týmto e-mailom prihlásia do Make It na webe alebo v aplikácii.",
    places: "Obsadené miesta: {used} z {seats}",
    validUntil: "Licencia platí do {date}.",
    expired: "Platnosť licencie skončila {date}. Ak ju chcete obnoviť, napíšte nám na {email}.",
    more: "Potrebujete viac miest? Napíšte nám na {email}.",
    schoolName: "Názov školy",
    rename: "Premenovať",
    add: "Pridať učiteľa",
    email: "E-mail",
    nameOpt: "Meno (nepovinné)",
    addBtn: "Pridať",
    remove: "Odobrať",
    removeTitle: "Odobrať {email}?",
    removeText: "Tento učiteľ už nebude mať Premium cez túto licenciu a miesto sa uvoľní. Jeho projekty sa neodstránia.",
    signedUp: "Prihlásený",
    notYet: "Zatiaľ neprihlásený",
    none: "Zatiaľ žiadni učitelia.",
    errSeats: "Nie sú k dispozícii žiadne voľné miesta.",
    errOther: "Tento e-mail už patrí inej škole.",
    errManager: "Toto je e-mail správcu školy.",
    errEmail: "Zadajte platný e-mail.",
    managerLine: "Školská licencia: učiteľov spravujete vy.",
  },
  hu: {
    manage: "Tanárok kezelése",
    intro: "Az iskolai licenced révén rajtad kívül {seats} tanár lehet a csapatodban. Add hozzá őket az e-mail-címükkel: Premium-felhasználók lesznek, amint ezzel az e-mail-címmel bejelentkeznek a Make It webes felületén vagy alkalmazásában.",
    places: "Foglalt helyek: {used}/{seats}",
    validUntil: "A licenc érvényes eddig: {date}.",
    expired: "A licenc lejárt: {date}. A megújításhoz írj nekünk: {email}.",
    more: "Több helyre van szükséged? Írj nekünk: {email}.",
    schoolName: "Iskola neve",
    rename: "Átnevezés",
    add: "Tanár hozzáadása",
    email: "E-mail",
    nameOpt: "Név (nem kötelező)",
    addBtn: "Hozzáadás",
    remove: "Eltávolítás",
    removeTitle: "Eltávolítod: {email}?",
    removeText: "Ezen a licencen keresztül már nem lesz Premium-felhasználó, és a helye felszabadul. A projektjei nem törlődnek.",
    signedUp: "Bejelentkezett",
    notYet: "Még nem jelentkezett be",
    none: "Még nincsenek tanárok.",
    errSeats: "Nincs több szabad hely.",
    errOther: "Ez az e-mail-cím már egy másik iskolához tartozik.",
    errManager: "Ez az iskola kezelőjének e-mail-címe.",
    errEmail: "Adj meg egy érvényes e-mail-címet.",
    managerLine: "Iskolai licenc: te kezeled a tanárokat.",
  },
  hr: {
    manage: "Upravljaj učiteljima",
    intro: "Tvoja školska licenca omogućuje ti da u timu, osim sebe, imaš {seats} učitelja. Dodaj ih preko adrese e-pošte: imat će Premium kad se tom adresom prijave u Make It, na webu ili u aplikaciji.",
    places: "Zauzeto mjesta: {used} od {seats}",
    validUntil: "Licenca vrijedi do {date}.",
    expired: "Licenca je istekla {date}. Za obnovu nam piši na {email}.",
    more: "Trebaš više mjesta? Piši nam na {email}.",
    schoolName: "Naziv škole",
    rename: "Preimenuj",
    add: "Dodaj učitelja",
    email: "E-pošta",
    nameOpt: "Ime (neobavezno)",
    addBtn: "Dodaj",
    remove: "Ukloni",
    removeTitle: "Ukloniti {email}?",
    removeText: "Ta osoba više neće imati Premium preko ove licence i mjesto će se osloboditi. Njezini projekti neće biti izbrisani.",
    signedUp: "Prijavljen",
    notYet: "Još nije prijavljen",
    none: "Još nema učitelja.",
    errSeats: "Nema više slobodnih mjesta.",
    errOther: "Ova adresa e-pošte već pripada drugoj školi.",
    errManager: "Ovo je adresa e-pošte upravitelja škole.",
    errEmail: "Upiši valjanu adresu e-pošte.",
    managerLine: "Školska licenca: ti upravljaš učiteljima.",
  },
  bg: {
    manage: "Управление на учителите",
    intro: "Вашият училищен лиценз ви позволява да имате {seats} учители в екипа си, освен вас. Добавете ги по имейл: те ще имат Premium, когато влязат в Make It с този имейл – в уеб версията или в приложението.",
    places: "Заети места: {used} от {seats}",
    validUntil: "Лицензът е валиден до {date}.",
    expired: "Лицензът е изтекъл на {date}. За да го подновите, пишете ни на {email}.",
    more: "Нужни са ви повече места? Пишете ни на {email}.",
    schoolName: "Име на училището",
    rename: "Преименуване",
    add: "Добавяне на учител",
    email: "Имейл",
    nameOpt: "Име (по избор)",
    addBtn: "Добавяне",
    remove: "Премахване",
    removeTitle: "Да се премахне ли {email}?",
    removeText: "Този учител вече няма да има Premium чрез този лиценз и мястото ще се освободи. Проектите му няма да бъдат изтрити.",
    signedUp: "Влязъл",
    notYet: "Все още не е влязъл",
    none: "Все още няма учители.",
    errSeats: "Няма свободни места.",
    errOther: "Този имейл вече принадлежи на друго училище.",
    errManager: "Това е имейлът на администратора на училището.",
    errEmail: "Въведете валиден имейл.",
    managerLine: "Училищен лиценз: вие управлявате учителите.",
  },
  da: {
    manage: "Administrer lærere",
    intro: "Din skolelicens giver plads til {seats} lærere på dit team ud over dig selv. Tilføj dem via e-mail: de bliver Premium, når de logger ind i Make It med den e-mail, på nettet eller i appen.",
    places: "{used} af {seats} pladser optaget",
    validUntil: "Licensen er gyldig indtil {date}.",
    expired: "Licensen udløb den {date}. Skriv til os på {email} for at forny den.",
    more: "Brug for flere pladser? Skriv til os på {email}.",
    schoolName: "Skolens navn",
    rename: "Omdøb",
    add: "Tilføj en lærer",
    email: "E-mail",
    nameOpt: "Navn (valgfrit)",
    addBtn: "Tilføj",
    remove: "Fjern",
    removeTitle: "Vil du fjerne {email}?",
    removeText: "Læreren er ikke længere Premium via denne licens, og pladsen bliver ledig. Lærerens projekter slettes ikke.",
    signedUp: "Logget ind",
    notYet: "Ikke logget ind endnu",
    none: "Ingen lærere endnu.",
    errSeats: "Der er ingen ledige pladser tilbage.",
    errOther: "Denne e-mail tilhører allerede en anden skole.",
    errManager: "Denne e-mail tilhører skolens administrator.",
    errEmail: "Indtast en gyldig e-mail.",
    managerLine: "Skolelicens: du administrerer lærerne.",
  },
  nb: {
    manage: "Administrer lærere",
    intro: "Med skolelisensen kan du ha {seats} lærere i teamet ditt, i tillegg til deg selv. Legg dem til med e-postadresse: de får Premium når de logger inn i Make It med den e-postadressen, på nettet eller i appen.",
    places: "{used} av {seats} plasser i bruk",
    validUntil: "Lisensen gjelder til {date}.",
    expired: "Lisensen utløp {date}. Skriv til oss på {email} for å fornye den.",
    more: "Trenger du flere plasser? Skriv til oss på {email}.",
    schoolName: "Skolens navn",
    rename: "Gi nytt navn",
    add: "Legg til en lærer",
    email: "E-post",
    nameOpt: "Navn (valgfritt)",
    addBtn: "Legg til",
    remove: "Fjern",
    removeTitle: "Vil du fjerne {email}?",
    removeText: "Læreren får ikke lenger Premium via denne lisensen, og plassen blir ledig. Prosjektene til læreren blir ikke slettet.",
    signedUp: "Har logget inn",
    notYet: "Har ikke logget inn ennå",
    none: "Ingen lærere ennå.",
    errSeats: "Det er ingen ledige plasser igjen.",
    errOther: "Denne e-postadressen tilhører allerede en annen skole.",
    errManager: "Denne e-postadressen tilhører skolens administrator.",
    errEmail: "Skriv inn en gyldig e-postadresse.",
    managerLine: "Skolelisens: du administrerer lærerne.",
  },
  fi: {
    manage: "Hallitse opettajia",
    intro: "Koulusi lisenssillä tiimiisi voi kuulua {seats} opettajaa sinun lisäksesi. Lisää heidät sähköpostiosoitteella: heillä on Premium, kun he kirjautuvat Make It -palveluun tällä sähköpostiosoitteella verkossa tai sovelluksessa.",
    places: "{used}/{seats} paikkaa käytössä",
    validUntil: "Lisenssi on voimassa {date} asti.",
    expired: "Lisenssi päättyi {date}. Jos haluat uusia sen, kirjoita meille osoitteeseen {email}.",
    more: "Tarvitsetko lisää paikkoja? Kirjoita meille osoitteeseen {email}.",
    schoolName: "Koulun nimi",
    rename: "Nimeä uudelleen",
    add: "Lisää opettaja",
    email: "Sähköposti",
    nameOpt: "Nimi (valinnainen)",
    addBtn: "Lisää",
    remove: "Poista",
    removeTitle: "Poistetaanko {email}?",
    removeText: "Hänellä ei ole enää Premiumia tämän lisenssin kautta, ja paikka vapautuu. Hänen projektejaan ei poisteta.",
    signedUp: "Kirjautunut",
    notYet: "Ei vielä kirjautunut",
    none: "Ei vielä opettajia.",
    errSeats: "Vapaita paikkoja ei ole jäljellä.",
    errOther: "Tämä sähköpostiosoite kuuluu jo toiseen kouluun.",
    errManager: "Tämä on koulun pääkäyttäjän sähköpostiosoite.",
    errEmail: "Anna kelvollinen sähköpostiosoite.",
    managerLine: "Koulun lisenssi: sinä hallinnoit opettajia.",
  },
  fa: {
    manage: "مدیریت معلمان",
    intro: "لایسنس مدرسه شما اجازه می‌دهد علاوه بر خودتان، {seats} معلم در تیمتان داشته باشید. آن‌ها را با ایمیل اضافه کنید: وقتی با همان ایمیل وارد Make It شوند، در وب یا در برنامه، Premium می‌شوند.",
    places: "{used} از {seats} جایگاه پر شده",
    validUntil: "لایسنس تا {date} معتبر است.",
    expired: "لایسنس در {date} منقضی شده است. برای تمدید آن، به {email} برای ما بنویسید.",
    more: "جایگاه بیشتری لازم دارید؟ به {email} برای ما بنویسید.",
    schoolName: "نام مدرسه",
    rename: "تغییر نام",
    add: "افزودن معلم",
    email: "ایمیل",
    nameOpt: "نام (اختیاری)",
    addBtn: "افزودن",
    remove: "حذف",
    removeTitle: "{email} حذف شود؟",
    removeText: "این معلم دیگر از طریق این لایسنس Premium نخواهد بود و جایگاهش آزاد می‌شود. پروژه‌هایش حذف نمی‌شوند.",
    signedUp: "وارد شده",
    notYet: "هنوز وارد نشده",
    none: "هنوز معلمی اضافه نشده است.",
    errSeats: "هیچ جایگاه خالی‌ای باقی نمانده است.",
    errOther: "این ایمیل از قبل متعلق به مدرسه دیگری است.",
    errManager: "این ایمیل متعلق به مدیر مدرسه است.",
    errEmail: "یک ایمیل معتبر وارد کنید.",
    managerLine: "لایسنس مدرسه: شما معلمان را مدیریت می‌کنید.",
  },
  ur: {
    manage: "اساتذہ کا انتظام کریں",
    intro: "آپ کا اسکول لائسنس آپ کو اپنے علاوہ اپنی ٹیم میں {seats} اساتذہ رکھنے کی اجازت دیتا ہے۔ انہیں ای میل سے شامل کریں: جب وہ اسی ای میل سے ویب یا ایپ میں Make It میں سائن ان کریں گے تو Premium ہو جائیں گے۔",
    places: "{seats} میں سے {used} نشستیں استعمال میں ہیں",
    validUntil: "لائسنس {date} تک کارآمد ہے۔",
    expired: "لائسنس کی میعاد {date} کو ختم ہو گئی۔ تجدید کے لیے ہمیں {email} پر لکھیں۔",
    more: "مزید نشستیں چاہییں؟ ہمیں {email} پر لکھیں۔",
    schoolName: "اسکول کا نام",
    rename: "نام تبدیل کریں",
    add: "استاد شامل کریں",
    email: "ای میل",
    nameOpt: "نام (اختیاری)",
    addBtn: "شامل کریں",
    remove: "ہٹائیں",
    removeTitle: "{email} کو ہٹائیں؟",
    removeText: "وہ اس لائسنس کے ذریعے مزید Premium نہیں رہیں گے اور نشست خالی ہو جائے گی۔ ان کے پروجیکٹس حذف نہیں ہوں گے۔",
    signedUp: "سائن ان ہو چکے ہیں",
    notYet: "ابھی سائن ان نہیں کیا",
    none: "ابھی کوئی استاد نہیں۔",
    errSeats: "کوئی خالی نشست باقی نہیں ہے۔",
    errOther: "یہ ای میل پہلے ہی کسی دوسرے اسکول سے منسلک ہے۔",
    errManager: "یہ ای میل اسکول منیجر کی ہے۔",
    errEmail: "درست ای میل درج کریں۔",
    managerLine: "اسکول لائسنس: اساتذہ کا انتظام آپ کرتے ہیں۔",
  },
  ms: {
    manage: "Urus guru",
    intro: "Lesen sekolah anda membolehkan anda mempunyai {seats} orang guru dalam pasukan anda, selain anda sendiri. Tambah mereka melalui e-mel: mereka akan menjadi Premium apabila log masuk ke Make It dengan e-mel itu, di web atau dalam aplikasi.",
    places: "{used} daripada {seats} tempat telah diisi",
    validUntil: "Lesen sah sehingga {date}.",
    expired: "Lesen telah tamat tempoh pada {date}. Untuk memperbaharuinya, hantar e-mel kepada kami di {email}.",
    more: "Perlukan lebih banyak tempat? Hantar e-mel kepada kami di {email}.",
    schoolName: "Nama sekolah",
    rename: "Namakan semula",
    add: "Tambah guru",
    email: "E-mel",
    nameOpt: "Nama (pilihan)",
    addBtn: "Tambah",
    remove: "Alih keluar",
    removeTitle: "Alih keluar {email}?",
    removeText: "Guru ini tidak lagi menjadi Premium melalui lesen ini dan tempatnya akan dikosongkan. Projek guru ini tidak dipadam.",
    signedUp: "Sudah log masuk",
    notYet: "Belum log masuk",
    none: "Belum ada guru.",
    errSeats: "Tiada lagi tempat kosong.",
    errOther: "E-mel ini sudah dimiliki oleh sekolah lain.",
    errManager: "E-mel ini milik pengurus sekolah.",
    errEmail: "Masukkan e-mel yang sah.",
    managerLine: "Lesen sekolah: anda mengurus guru.",
  },
  az: {
    manage: "Müəllimləri idarə et",
    intro: "Məktəb lisenziyanız sizdən əlavə komandanızda {seats} müəllimin olmasına imkan verir. Onları e-poçt ünvanı ilə əlavə edin: həmin e-poçtla vebdə və ya tətbiqdə Make It-ə daxil olduqda Premium olacaqlar.",
    places: "{seats} yerdən {used} yer tutulub",
    validUntil: "Lisenziya {date} tarixinədək etibarlıdır.",
    expired: "Lisenziyanın müddəti {date} tarixində bitib. Yeniləmək üçün bizə {email} ünvanına yazın.",
    more: "Daha çox yer lazımdır? Bizə {email} ünvanına yazın.",
    schoolName: "Məktəbin adı",
    rename: "Adını dəyiş",
    add: "Müəllim əlavə et",
    email: "E-poçt",
    nameOpt: "Ad (istəyə bağlı)",
    addBtn: "Əlavə et",
    remove: "Çıxar",
    removeTitle: "{email} çıxarılsın?",
    removeText: "Bu müəllim artıq bu lisenziya ilə Premium olmayacaq və yer boşalacaq. Onun layihələri silinmir.",
    signedUp: "Daxil olub",
    notYet: "Hələ daxil olmayıb",
    none: "Hələ müəllim yoxdur.",
    errSeats: "Boş yer qalmayıb.",
    errOther: "Bu e-poçt artıq başqa məktəbə aiddir.",
    errManager: "Bu e-poçt məktəb menecerinə məxsusdur.",
    errEmail: "Düzgün e-poçt ünvanı daxil edin.",
    managerLine: "Məktəb lisenziyası: müəllimləri siz idarə edirsiniz.",
  },
  uz: {
    manage: "Oʻqituvchilarni boshqarish",
    intro: "Maktab litsenziyangiz jamoangizda sizdan tashqari yana {seats} ta oʻqituvchi boʻlishiga imkon beradi. Ularni e-pochta orqali qoʻshing: ular shu e-pochta bilan vebda yoki ilovada Make It tizimiga kirishlari bilan Premium boʻladi.",
    places: "{seats} ta oʻrindan {used} tasi band",
    validUntil: "Litsenziya {date} sanasigacha amal qiladi.",
    expired: "Litsenziya muddati {date} sanasida tugagan. Uni yangilash uchun bizga {email} manziliga yozing.",
    more: "Koʻproq oʻrin kerakmi? Bizga {email} manziliga yozing.",
    schoolName: "Maktab nomi",
    rename: "Nomini oʻzgartirish",
    add: "Oʻqituvchi qoʻshish",
    email: "E-pochta",
    nameOpt: "Ism (ixtiyoriy)",
    addBtn: "Qoʻshish",
    remove: "Olib tashlash",
    removeTitle: "{email} olib tashlansinmi?",
    removeText: "U endi bu litsenziya orqali Premium boʻlmaydi va oʻrin boʻshaydi. Uning loyihalari oʻchirilmaydi.",
    signedUp: "Kirgan",
    notYet: "Hali kirmagan",
    none: "Hali oʻqituvchilar yoʻq.",
    errSeats: "Boʻsh oʻrinlar qolmadi.",
    errOther: "Bu e-pochta boshqa maktabga tegishli.",
    errManager: "Bu e-pochta maktab administratoriga tegishli.",
    errEmail: "Toʻgʻri e-pochta manzilini kiriting.",
    managerLine: "Maktab litsenziyasi: oʻqituvchilarni siz boshqarasiz.",
  },
  fil: {
    manage: "Pamahalaan ang mga guro",
    intro: "Sa iyong school license, puwede kang magkaroon ng {seats} guro sa iyong team, bukod sa iyo. Idagdag sila gamit ang email: magiging Premium sila kapag nag-sign in sila sa Make It gamit ang email na iyon, sa web man o sa app.",
    places: "{used} sa {seats} slot ang nagamit na",
    validUntil: "Valid ang license hanggang {date}.",
    expired: "Nag-expire ang license noong {date}. Para i-renew ito, sumulat sa amin sa {email}.",
    more: "Kailangan mo ng mas maraming slot? Sumulat sa amin sa {email}.",
    schoolName: "Pangalan ng paaralan",
    rename: "Palitan ang pangalan",
    add: "Magdagdag ng guro",
    email: "Email",
    nameOpt: "Pangalan (opsyonal)",
    addBtn: "Idagdag",
    remove: "Alisin",
    removeTitle: "Alisin si {email}?",
    removeText: "Hindi na siya magiging Premium sa license na ito at mababakante ang slot. Hindi ide-delete ang kanyang mga proyekto.",
    signedUp: "Naka-sign in na",
    notYet: "Hindi pa nakaka-sign in",
    none: "Wala pang guro.",
    errSeats: "Wala nang bakanteng slot.",
    errOther: "Kabilang na ang email na ito sa ibang paaralan.",
    errManager: "Email ito ng manager ng paaralan.",
    errEmail: "Maglagay ng valid na email.",
    managerLine: "School license: ikaw ang namamahala sa mga guro.",
  },
};

/** A text of the school pages, with {placeholders} filled in. */
export function st(key: Key, vars: Record<string, string | number> = {}): string {
  const text = (TEXTS[currentLanguage()] ?? TEXTS.en)[key] ?? TEXTS.en[key];
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** The manageSchool error codes the manager can do something about. */
export function schoolErrorText(code: string): string | null {
  switch (code) {
    case "no-seats":
      return st("errSeats");
    case "in-another-school":
      return st("errOther");
    case "is-manager":
      return st("errManager");
    case "bad-email":
      return st("errEmail");
    default:
      return null;
  }
}
